import { useId } from 'react';
import {
  Alert,
  ButtonLink,
  Card,
  CardBody,
  CardHeader,
  Link,
  Text,
} from '@backstage/ui';
import { CodeSnippet, MarkdownContent } from '@backstage/core-components';
import { iconsApiRef, useApi } from '@backstage/frontend-plugin-api';
import type { ScaffolderTaskOutput } from '@backstage/plugin-scaffolder-react';
import { DefaultTemplateOutputs } from '@backstage/plugin-scaffolder-react/alpha';
import type { RemixiconComponentType } from '@remixicon/react';
import { waadooIcons } from '../ui/icons/waadooIcons';
import { CardTitle, CopyCommandButton } from '../ui';
import {
  rowGroups,
  type GroupRowView,
  type ReportData,
  type RowGroupView,
} from './deletionTask';

/** The tone of a status: BUI's status tokens. */
type Tone = 'info' | 'success' | 'warning' | 'danger';

/**
 * One line of the report's inline markdown (`code`, **bold**, links), the
 * paragraph's margins removed by `.waadoo-md-inline` (styles.css). Rendered
 * by MarkdownContent, as the scaffolder's own text outputs are: sanitised,
 * in-app links resolved by the app's router.
 */
function InlineMarkdown(props: { text: string }) {
  return <MarkdownContent className="waadoo-md-inline" content={props.text} />;
}

function resultAlert(report: ReportData): {
  status: 'info' | 'success' | 'warning';
  title: string;
} {
  if (report.mode === 'simulation') {
    return report.outcome === 'ok'
      ? { status: 'info', title: 'Simulation terminée — rien n’a été modifié' }
      : {
          status: 'warning',
          title:
            'Simulation terminée avec des points à vérifier — rien n’a été modifié',
        };
  }
  return report.outcome === 'ok'
    ? { status: 'success', title: 'Suppression terminée' }
    : {
        status: 'warning',
        title: 'Suppression terminée avec des points à reprendre',
      };
}

function Figures(props: { report: ReportData }) {
  const { report } = props;
  const tiles: Array<{ value: number; label: string; tone: Tone }> = [
    {
      value: report.figures.removed,
      label: report.mode === 'simulation' ? 'Seraient supprimés' : 'Supprimés',
      // Would be removed: the info tone; removed: the danger tone.
      tone: report.mode === 'simulation' ? 'info' : 'danger',
    },
    {
      value: report.figures.noop,
      label: 'Déjà absents / sans objet',
      tone: 'success',
    },
    {
      value: report.figures.manual,
      label: 'À faire à la main',
      tone: 'warning',
    },
  ];
  return (
    <ul className="waadoo-deletion-report__figures" aria-label="Chiffres clés">
      {tiles.map(tile => (
        <li
          key={tile.label}
          className="waadoo-deletion-report__figure"
          data-tone={tile.tone}
        >
          <strong>{tile.value}</strong>
          <span>{tile.label}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The glyphs of the row icons the backend names (`RowIcon`, backend
 * `projectDelete/finalReport.ts`), from the app's vocabulary; `gitlab`,
 * `argocd` and `grafana` are the official logos of the icons registry
 * (modules/icons.tsx).
 */
const ROW_GLYPHS: Record<string, RemixiconComponentType> = {
  bucket: waadooIcons.bucket,
  crossplane: waadooIcons.crossplaneResource,
  lldap: waadooIcons.directoryGroup,
  catalog: waadooIcons.catalog,
  file: waadooIcons.repositoryFile,
};
const ROW_LOGOS = ['gitlab', 'argocd', 'grafana'];

/** A row's icon, decorative (its element's name says the same); none if unknown. */
function RowIcon(props: { icon?: string }) {
  const iconsApi = useApi(iconsApiRef);
  const { icon } = props;
  if (icon === undefined) return null;
  if (ROW_LOGOS.includes(icon)) return <>{iconsApi.icon(icon)}</>;
  const Glyph = ROW_GLYPHS[icon];
  return Glyph ? <Glyph aria-hidden focusable="false" /> : null;
}

function GroupRow(props: { row: GroupRowView }) {
  const { row } = props;
  return (
    <li className="waadoo-deletion-report__row">
      <span className="waadoo-deletion-report__row-icon" aria-hidden>
        <RowIcon icon={row.icon} />
      </span>
      <span className="waadoo-deletion-report__row-element">
        <InlineMarkdown text={row.element} />
      </span>
      <span className="waadoo-deletion-report__chips">
        {row.items.map(item => (
          <span key={item} className="waadoo-deletion-report__chip">
            {row.markdown ? <InlineMarkdown text={item} /> : item}
          </span>
        ))}
      </span>
    </li>
  );
}

/** One group of « Ce qui serait fait / a été fait »: its pill, its count, its rows. */
function Group(props: { group: RowGroupView }) {
  const { group } = props;
  const titleId = useId();
  return (
    <section
      className="waadoo-deletion-report__group"
      aria-labelledby={titleId}
    >
      <div className="waadoo-deletion-report__group-header">
        <span
          id={titleId}
          className="waadoo-deletion-report__pill"
          data-tone={group.tone}
        >
          {group.title}
        </span>
        <span className="waadoo-deletion-report__group-count">
          {group.rows.length} élément(s)
        </span>
      </div>
      <ul className="waadoo-deletion-report__rows">
        {group.rows.map((row, index) => (
          <GroupRow key={index} row={row} />
        ))}
      </ul>
    </section>
  );
}

function ManualItem(props: { item: ReportData['manual'][number] }) {
  const { item } = props;
  return (
    <>
      <div className="waadoo-deletion-report__todo-line">
        <InlineMarkdown text={item.text} />
        {item.links?.map(link => (
          <span key={link.url}>
            {' — '}
            <Link href={link.url} target="_blank" rel="noopener noreferrer">
              {link.label}
            </Link>
          </span>
        ))}
      </div>
      {item.commands && item.commands.length > 0 && (
        <details className="waadoo-deletion-report__commands">
          <summary>Afficher les commandes</summary>
          {item.commands.map((command, index) => (
            <div key={index} className="waadoo-deletion-report__command">
              {command.intro && <InlineMarkdown text={command.intro} />}
              <div className="waadoo-deletion-report__code">
                {/* Room on the right for the copy button: the lines wrap
                    before it rather than under it. */}
                <CodeSnippet
                  text={command.shell}
                  language="bash"
                  wrapLongLines
                  customStyle={{ paddingRight: '44px' }}
                />
                <CopyCommandButton text={command.shell} />
              </div>
            </div>
          ))}
        </details>
      )}
    </>
  );
}

/**
 * The step-by-step details and the starting inventory: the template's text
 * outputs but its first, the markdown « Compte rendu » that the report above
 * replaces — as the scaffolder renders them (DefaultTemplateOutputs: one
 * button per output, then the selected one).
 */
function DetailOutputs(props: { output: ScaffolderTaskOutput }) {
  const text = props.output.text ?? [];
  if (text.length < 2) return null;
  return (
    <details className="waadoo-deletion-report__details">
      <summary>Détail des étapes et inventaire de départ</summary>
      <DefaultTemplateOutputs output={{ text: text.slice(1) }} />
    </details>
  );
}

/**
 * The final report of a project deletion, from the cleanup step's
 * `reportData` (backend `projectDelete/finalReport.ts`): the result, the key
 * figures, what needs attention, « Ce qui serait fait / a été fait » by kind
 * of action, « À faire à la main » numbered — the links after each sentence,
 * the commands folded with a copy button each — « À savoir », and after a
 * simulation the way to the real deletion. The template's markdown outputs
 * stay, folded, at the end.
 */
export function DeletionReport(props: {
  report: ReportData;
  output: ScaffolderTaskOutput;
}) {
  const { report, output } = props;
  const result = resultAlert(report);
  const DeleteIcon = waadooIcons.delete;
  return (
    <div className="waadoo-deletion-report">
      <Alert status={result.status} icon title={result.title} role="status" />
      <Card>
        <CardHeader>
          <CardTitle level={3} title={<>Compte rendu — {report.project}</>} />
        </CardHeader>
        <CardBody>
          <div className="waadoo-deletion-report__body">
            <Figures report={report} />

            {report.warnings.map((warning, index) => (
              <Alert
                key={index}
                status="warning"
                icon
                title="Attention"
                description={<InlineMarkdown text={warning} />}
              />
            ))}

            <section>
              <Text as="h4" variant="body-large" weight="bold">
                {report.mode === 'simulation'
                  ? 'Ce qui serait fait'
                  : 'Ce qui a été fait'}
              </Text>
              <div className="waadoo-deletion-report__groups">
                {rowGroups(report).map(group => (
                  <Group key={group.kind} group={group} />
                ))}
              </div>
            </section>

            <section>
              <Text as="h4" variant="body-large" weight="bold">
                À faire à la main
              </Text>
              {report.manual.length === 0 ? (
                <Text as="p" variant="body-medium" color="secondary">
                  Rien à faire à la main.
                </Text>
              ) : (
                <ol className="waadoo-deletion-report__todo">
                  {report.manual.map((item, index) => (
                    <li key={index}>
                      <span
                        className="waadoo-deletion-report__todo-number"
                        aria-hidden
                      >
                        {index + 1}
                      </span>
                      <div className="waadoo-deletion-report__todo-body">
                        <ManualItem item={item} />
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            {report.notes.length > 0 && (
              <section>
                <Text as="h4" variant="body-large" weight="bold">
                  À savoir
                </Text>
                <ul className="waadoo-deletion-report__notes">
                  {report.notes.map((note, index) => (
                    <li key={index}>
                      <InlineMarkdown text={note} />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {report.mode === 'simulation' && report.nextStepHref && (
              <section className="waadoo-deletion-report__next">
                <div>
                  <Text as="h4" variant="body-medium" weight="bold">
                    Prochaine étape
                  </Text>
                  <Text as="p" variant="body-small" color="secondary">
                    Relisez ce compte rendu, puis lancez la suppression réelle.
                  </Text>
                </div>
                {/* The danger mark of every destructive button (« P3 »,
                    styles.css): ButtonLink has no `destructive` prop, the
                    attribute BUI would set is passed as is. */}
                <ButtonLink
                  variant="primary"
                  data-destructive="true"
                  href={report.nextStepHref}
                  iconStart={<DeleteIcon aria-hidden focusable="false" />}
                >
                  Lancer la suppression réelle…
                </ButtonLink>
              </section>
            )}

            <DetailOutputs output={output} />
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
